import React from 'react';
import { INewsletterModalContent } from '../../interfaces/INewsletter';
import { IPageDataReducer } from '../../reducers/pageData';
import { fetchget } from '../../helpers/http';

import { MarketContext } from '../../context/markets';
import { signUp, updateNewsletterPopUpData } from '../../actions/newsletterAction';

import { NewsletterModalSubscribe } from './NewsletterModalSubscribe';
import { NewsletterModalSuccess } from './NewsletterModalSuccess';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { setNewsletterModalSessionDismissed } from '../../helpers/cookie';

const getNewsletterModalData = async (host: string): Promise<INewsletterModalContent | null> => {
    try {
        const resp = await fetchget(`${host}/newsletter/modal`);

        const hasRequiredFields =
            resp?.title &&
            resp?.subtitle &&
            resp?.button?.text &&
            resp?.badge &&
            resp?.badge?.status !== undefined;

        if (!hasRequiredFields) {
            return null;
        }

        return resp;
    } catch (error) {
        return null;
    }
};

export const NewsletterModal = ({ pageData }: { pageData?: IPageDataReducer }) => {
    const [modalContent, setModalContent] = React.useState<null | INewsletterModalContent>(null);
    const [step, setStep] = React.useState<'main' | 'success' | null>(null);

    const { markets } = React.useContext(MarketContext);
    const { isDreamlinesDE, isCruise1stUK } = markets;

    const isDataLoaded = React.useRef(false);

    const dispatch = useDispatch();

    const newsletterPage = useSelector((state: RootStateOrAny) => state.newsletterPage);
    const { popUp } = newsletterPage;
    const { shouldShowNewsletterModal } = popUp;

    const onCloseHandler = () => {
        if (step === 'main') {
            setNewsletterModalSessionDismissed();
            setStep(null);
        } else if (step === 'success') {
            setStep(null);
        }
    };

    const onSubmit = async (email: string) => {
        const exiprationDays = 365;

        try {
            await dispatch(signUp(email, `${pageData.brandConfig.portalId} popup`, exiprationDays));
            setStep('success');

            // hide newsletter sso banner if it's shown
            dispatch(
                updateNewsletterPopUpData({
                    popUp: {
                        ...popUp,
                        shouldShowNewsletterBanner: false,
                        timeout: 0,
                    },
                }),
            );
        } catch (error) {
            console.error('Newsletter subscription failed', error);
        }
    };

    React.useEffect(() => {
        const fetchData = async () => {
            const data = await getNewsletterModalData(pageData.appConfig.apiHost);

            setModalContent(data);
            isDataLoaded.current = true;

            if (data) {
                setStep('main');
            }
        };

        if (!pageData || !shouldShowNewsletterModal) return;

        if (!isDataLoaded.current) {
            fetchData();
        }
    }, [shouldShowNewsletterModal]);

    if (!shouldShowNewsletterModal || !modalContent) {
        return null;
    }

    return (
        <>
            {step === 'main' && modalContent && (
                <NewsletterModalSubscribe
                    title={modalContent.title}
                    description={modalContent.subtitle}
                    buttonText={modalContent.button.text}
                    badgeConfig={modalContent.badge}
                    image={modalContent.image}
                    onSubmit={onSubmit}
                    onCloseHandler={onCloseHandler}
                />
            )}
            {step === 'success' && (
                <NewsletterModalSuccess
                    enableCatalogueSignUp={isCruise1stUK || isDreamlinesDE}
                    onBannerCloseHandler={onCloseHandler}
                />
            )}
        </>
    );
};
